import { useState, useEffect, useCallback, useMemo } from "react";
import { MergeResolutions } from "../../ImportTable.types";
import { formatValueForDisplay } from "../../ImportTable.helpers";
import { getDealLabel } from "../../../../../../utilities/utils";
import { MergeConflictModalProps, MergeConflictModalViewModel } from "./MergeConflictModal.types";

// Fields that allow custom text entry (simple text/string fields)
const TEXT_FIELDS: Set<string> = new Set([
	"importGrowerName",
	"importFarmName",
	"importFieldName",
	"fieldNotes",
	"eventId",
	"labSubmissionCode",
	"lastCrop",
	"nextCrop",
	"ssurgoMapUnitName",
	"salespersonMobilePhone",
	"submitterEmail",
	"salespersonEmailAddress",
]);

function initializeSelections(conflicts: MergeConflictModalProps["conflicts"]): MergeResolutions {
	const initial: MergeResolutions = {};
	for (const conflict of conflicts) {
		initial[conflict.propertyKey] = conflict.options[0]?.value;
	}
	return initial;
}

export function useMergeConflictModalViewModel(props: MergeConflictModalProps): MergeConflictModalViewModel {
	const { conflicts, onConfirm, salespersons, branches, deals } = props;

	const [selections, setSelections] = useState<MergeResolutions>(() => initializeSelections(conflicts));
	const [customTextValues, setCustomTextValues] = useState<Record<string, string>>({});
	const [isUsingCustomText, setIsUsingCustomText] = useState<Record<string, boolean>>({});

	// Reset selections when conflicts change
	useEffect(() => {
		setSelections(initializeSelections(conflicts));
		setCustomTextValues({});
		setIsUsingCustomText({});
	}, [conflicts]);

	const handleSelectionChange = useCallback((propertyKey: string, value: unknown) => {
		setSelections((prev) => ({
			...prev,
			[propertyKey]: value,
		}));
		// Clear custom text flag when selecting an existing option
		setIsUsingCustomText((prev) => ({
			...prev,
			[propertyKey]: false,
		}));
	}, []);

	const handleCustomTextChange = useCallback((propertyKey: string, value: string) => {
		setCustomTextValues((prev) => ({
			...prev,
			[propertyKey]: value,
		}));
	}, []);

	const handleToggleCustomText = useCallback((propertyKey: string, useCustom: boolean) => {
		setIsUsingCustomText((prev) => ({
			...prev,
			[propertyKey]: useCustom,
		}));
	}, []);

	const isTextField = useCallback((propertyKey: string): boolean => {
		return TEXT_FIELDS.has(propertyKey);
	}, []);

	const handleConfirm = useCallback(() => {
		// Build final resolutions, using custom text where applicable
		const finalResolutions: MergeResolutions = {};
		for (const key of Object.keys(selections)) {
			if (isUsingCustomText[key] && customTextValues[key] !== undefined) {
				finalResolutions[key] = customTextValues[key];
			} else {
				finalResolutions[key] = selections[key];
			}
		}
		onConfirm(finalResolutions);
	}, [onConfirm, selections, isUsingCustomText, customTextValues]);

	const allResolved = useMemo(
		() => conflicts.every((conflict) => conflict.propertyKey in selections),
		[conflicts, selections],
	);

	const formatValue = useCallback(
		(propertyKey: string, value: unknown): string => {
			// For salespersonIds, resolve IDs to names
			if (propertyKey === "salespersonIds" && Array.isArray(value) && salespersons) {
				const names = value.map((id) => salespersons.find((sp) => sp.id === id)?.name).filter(Boolean);
				return names.length > 0 ? names.join(", ") : formatValueForDisplay(value);
			}
			// For branchIds, resolve IDs to names
			if (propertyKey === "branchIds" && Array.isArray(value) && branches) {
				const names = value.map((id) => branches.find((b) => b.id === id)?.name).filter(Boolean);
				return names.length > 0 ? names.join(", ") : formatValueForDisplay(value);
			}
			// For dealIds, resolve IDs to deal labels
			if (propertyKey === "dealIds" && Array.isArray(value) && deals) {
				const labels = value
					.map((id) => {
						if (id === "custom") return "No linked Configuration";
						const deal = deals.find((d) => d.id === id);
						return deal ? getDealLabel(deal) : null;
					})
					.filter(Boolean);
				return labels.length > 0 ? labels.join(", ") : formatValueForDisplay(value);
			}
			return formatValueForDisplay(value);
		},
		[salespersons, branches, deals],
	);

	return {
		selections,
		allResolved,
		handleSelectionChange,
		handleConfirm,
		formatValue,
		customTextValues,
		isUsingCustomText,
		handleCustomTextChange,
		handleToggleCustomText,
		isTextField,
	};
}
